/**
 * Route + UI guards.
 *
 * - `<Authenticated>` — wraps protected routes; redirects unauthenticated
 *   users to `/login?next=<original-path>` so they land back where they
 *   started post-login.
 * - `<RequirePermission perm="catalog:author">` — UX gate; renders the children
 *   only if the caller's SERVER-RESOLVED permissions include `perm`. NOT the JWT
 *   claim — IAM never puts permission values in a token, so gating on the claim
 *   hid every authoring page from everyone. The backend is the real enforcement
 *   point per ADR-0026; this guard is cosmetic (hides CTAs the user can't act on).
 */

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useIAMAuth } from "@/auth/iam";
import { useResolvedPermissions, type GatedPermission } from "@/auth/permissions";
import { wasSignedOut } from "@/auth/tokens";

interface AuthenticatedProps {
  children: React.ReactNode;
}

/**
 * Redirect unauthenticated visitors to `/login?next=<path>`. Renders the
 * children only when `isAuthenticated` is true; renders nothing while a
 * redirect is in flight.
 *
 * A visitor who has just signed out goes to `/signed-out` instead. IAM's end-session
 * returns the browser to the origin, a protected route, and sending it on to `/login`
 * would start a new sign-in nobody asked for (docs#15 note 16577).
 */
export function Authenticated({ children }: AuthenticatedProps): JSX.Element | null {
  const { isAuthenticated, isReady } = useIAMAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isReady && !isAuthenticated) {
      if (wasSignedOut()) {
        navigate("/signed-out", { replace: true });
        return;
      }
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/login?next=${next}`, { replace: true });
    }
  }, [isAuthenticated, isReady, location.pathname, location.search, navigate]);

  if (!isAuthenticated) {
    return null;
  }
  return <>{children}</>;
}

interface RequirePermissionProps {
  /**
   * A gated permission (ADR-0026), checked against the caller's SERVER-RESOLVED
   * permissions. Typed to the known set so an unmapped key is a compile error,
   * not a silent fail-closed — see `GatedPermission`.
   */
  perm: GatedPermission;
  children: React.ReactNode;
  /** What to render when the permission is absent. Defaults to nothing. */
  fallback?: React.ReactNode;
}

/**
 * Cosmetic permission gate. Backend re-checks per ADR-0026; this just hides CTAs
 * the user couldn't act on anyway.
 *
 * ⚠️ Permissions are RESOLVED from IAM, not read off the JWT. The token carries
 * no permission values, so the old `permissions.includes(perm)` was `false` for
 * everyone — this guard rendered `fallback` (NotFound) over every authoring page
 * against real IAM. While the resolve is in flight we render nothing rather than
 * flash the page and yank it, and a failed resolve falls closed to `fallback`.
 */
export function RequirePermission({
  perm,
  children,
  fallback = null,
}: RequirePermissionProps): JSX.Element {
  const { isReady, has } = useResolvedPermissions();
  if (!isReady) {
    return <></>;
  }
  if (!has(perm)) {
    return <>{fallback}</>;
  }
  return <>{children}</>;
}

interface RequireClientAdminProps {
  children: React.ReactNode;
  /**
   * The server-resolved permission this portal route needs. Defaults to
   * `clients:manage`, which is what every portal user-admin, approvals and
   * LIS-message route enforces. `/portal/audit` passes `audit:read`, because that
   * is the one portal route the backend gates on `AUDIT_READ` instead.
   */
  perm?: GatedPermission;
  /** Rendered when the caller does not hold `perm`. Defaults to nothing. */
  fallback?: React.ReactNode;
  /** Rendered when the permission is held but the tenant claim is missing. */
  misconfigured?: React.ReactNode;
  /**
   * Rendered when the permission resolve FAILED. Kept apart from `fallback`
   * because we do not know the caller lacks access, and telling a real admin
   * "not found" during an IAM outage sends them to the wrong fix.
   */
  unavailable?: React.ReactNode;
}

/**
 * Client Portal gate (phase2-client-portal AC#1/AC#2).
 *
 * **Gated on the SERVER-RESOLVED permission, exactly what the backend enforces.**
 * Until 2026-09-30 this compared the token's `role` claim to `ClientAdmin`. Real
 * IAM puts no `role` on an invite-mode portal user (that claim carries ASP.NET
 * Identity roles, assigned only by seeders), so every real portal admin landed on
 * the not-found page, while the backend never looks at `role` for the portal at
 * all: it resolves `smart-reports-config:clients:tenants:manage` from IAM
 * `/api/me/permissions` and scopes by `organization_unit_id`. The role claim is
 * now ignored here on purpose. Admitting on it would show a portal whose every
 * call 403s, and refusing on its absence is the bug being fixed.
 *
 * Four outcomes, kept distinct because they mean different things:
 *
 * - **Resolve in flight** → render nothing, so there is no flash of not-found.
 * - **Resolve failed** → `unavailable`. We do not know what they hold.
 * - **Permission absent** → `fallback`. Not a tenant admin.
 * - **Permission held, no `organization_unit_id`** → `misconfigured`. The backend
 *   403s every portal call without the tenant claim (`_tenant_of`). Rendering an
 *   empty portal would read as "you have no users", and a default org would be a
 *   cross-tenant leak. Say "tenant misconfigured" and stop.
 *
 * Cosmetic, as ever: the backend re-checks the permission and derives the org
 * from the same claim. This guard only avoids showing a screen that would 403 on
 * every call, or hiding one that would work.
 */
export function RequireClientAdmin({
  children,
  perm = "clients:manage",
  fallback = null,
  misconfigured = null,
  unavailable = null,
}: RequireClientAdminProps): JSX.Element {
  const { organizationId } = useIAMAuth();
  const { isReady, isError, has } = useResolvedPermissions();
  if (!isReady) {
    return <></>;
  }
  if (isError) {
    return <>{unavailable}</>;
  }
  if (!has(perm)) {
    return <>{fallback}</>;
  }
  if (organizationId === null || organizationId === "") {
    return <>{misconfigured}</>;
  }
  return <>{children}</>;
}

interface RequireMedicusStaffProps {
  /** The server-resolved permission the page's backend checks. */
  perm: GatedPermission;
  children: React.ReactNode;
  /** Rendered for anyone else, a lab user included. Defaults to nothing. */
  fallback?: React.ReactNode;
}

/**
 * Medicus staff pages (`/admin/*`), docs#239 item 6.
 *
 * Staff is a token with NO `organization_unit_id`, the same line `useTenantScope`
 * draws and the one the backends draw: the audit service pins any token carrying an
 * organisation to that organisation. A lab admin holds `audit:read` through IAM's
 * ClientAdmin bundle, so gating `/admin/audit` on the permission alone showed her a
 * page titled "all tenants" listing only her own lab. Her audit page is
 * `/portal/audit`. Cosmetic, as ever: the backends enforce the scope.
 */
export function RequireMedicusStaff({
  perm,
  children,
  fallback = null,
}: RequireMedicusStaffProps): JSX.Element {
  const { organizationId } = useIAMAuth();
  const { isReady, has } = useResolvedPermissions();
  if (!isReady) {
    return <></>;
  }
  if (!has(perm) || (organizationId !== null && organizationId !== "")) {
    return <>{fallback}</>;
  }
  return <>{children}</>;
}
